import React, { useState } from 'react';

interface AddOfficialFormProps {
  adminKey: string;
  onSuccess?: (official: any) => void;
  onCancel?: () => void;
}

const personTypes = [
  { value: 'official', label: 'Elected Official', description: 'Senator, Representative, Governor, etc.' },
  { value: 'ceo', label: 'CEO', description: 'Chief Executive Officer' },
  { value: 'executive', label: 'Executive', description: 'Corporate executive or C-suite' },
  { value: 'corporate', label: 'Corporate Leader', description: 'Board member, major shareholder' },
  { value: 'donor', label: 'Major Donor', description: 'Political mega-donor' },
  { value: 'lobbyist', label: 'Lobbyist', description: 'Professional lobbyist or influence agent' },
];

const officeLevels = [
  { value: 'federal', label: 'Federal' },
  { value: 'state', label: 'State' },
  { value: 'local', label: 'Local' },
  { value: 'corporate', label: 'Corporate' },
];

const officeBranches = [
  { value: 'executive', label: 'Executive' },
  { value: 'legislative', label: 'Legislative' },
  { value: 'judicial', label: 'Judicial' },
  { value: 'private', label: 'Private Sector' },
];

const chambers = [
  { value: 'senate', label: 'Senate' },
  { value: 'house', label: 'House' },
];

const parties = [
  { value: 'Republican', label: 'Republican' },
  { value: 'Democrat', label: 'Democrat' },
  { value: 'Independent', label: 'Independent' },
  { value: 'Libertarian', label: 'Libertarian' },
  { value: 'Green', label: 'Green' },
  { value: 'Other', label: 'Other' },
];

export default function AddOfficialForm({ adminKey, onSuccess, onCancel }: AddOfficialFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    // Required fields
    id: '',
    name: '',
    first_name: '',
    last_name: '',
    office_name: '',
    person_type: 'official',

    // Optional fields
    office_level: '',
    office_branch: '',
    party: '',
    state: '',
    district: '',
    chamber: '',
    photo_url: '',
    website: '',
    email: '',
    phone: '',
    office_address: '',
    bioguide_id: '',
    openstates_id: '',
    ticker: '',
  });

  // Auto-generate ID based on name and type
  const generateId = () => {
    if (!formData.name || !formData.person_type) return;

    const namePart = formData.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    const typePart = formData.person_type;
    const timestamp = Date.now().toString(36).slice(-4);

    setFormData({ ...formData, id: `${typePart}-${namePart}-${timestamp}` });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!formData.id || !formData.name || !formData.office_name) {
      setError('Please fill in ID, Name, and Office/Position');
      return;
    }

    setLoading(true);

    try {
      // Determine default office_level based on person_type if not provided
      let finalOfficeLevel = formData.office_level;
      if (!finalOfficeLevel) {
        const isCorporate = ['ceo', 'executive', 'corporate', 'donor', 'lobbyist'].includes(formData.person_type);
        finalOfficeLevel = isCorporate ? 'corporate' : 'federal';
      }

      // Determine default office_branch based on person_type if not provided
      let finalOfficeBranch = formData.office_branch;
      if (!finalOfficeBranch) {
        const isCorporate = ['ceo', 'executive', 'corporate'].includes(formData.person_type);
        finalOfficeBranch = isCorporate ? 'private' : 'legislative';
      }

      const response = await fetch('/api/officials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminKey}`,
        },
        body: JSON.stringify({
          ...formData,
          // Ensure office_level and office_branch always have values
          office_level: finalOfficeLevel,
          office_branch: finalOfficeBranch,
          // Only send non-empty optional fields
          party: formData.party || null,
          state: formData.state || null,
          district: formData.district || null,
          chamber: formData.chamber || null,
          photo_url: formData.photo_url || null,
          website: formData.website || null,
          email: formData.email || null,
          phone: formData.phone || null,
          office_address: formData.office_address || null,
          bioguide_id: formData.bioguide_id || null,
          openstates_id: formData.openstates_id || null,
          ticker: formData.ticker || null,
          source: 'manual',
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create official');
      }

      const data = await response.json();
      setSuccess(true);

      if (onSuccess) {
        setTimeout(() => onSuccess(data.official), 1500);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create official');
    } finally {
      setLoading(false);
    }
  };

  const isCorporate = ['ceo', 'executive', 'corporate'].includes(formData.person_type);

  if (success) {
    return (
      <div className="bg-white dark:bg-navy-800/40 border border-emerald-500/30 rounded-xl p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-black text-navy-900 dark:text-cream-50 mb-2">Official Added!</h3>
        <p className="text-sm text-navy-600 dark:text-cream-200 mb-4">
          {formData.name} has been added to the database.
        </p>
        <p className="text-xs text-navy-500 dark:text-navy-300">
          You can now run intelligence agents to build their dossier.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-cream-300 dark:border-navy-600 bg-cream-50 dark:bg-navy-800/20">
        <h3 className="text-sm font-bold text-navy-900 dark:text-cream-50">Add New Official/CEO</h3>
        <p className="text-xs text-navy-600 dark:text-cream-300 mt-1">
          Manually add an elected official, CEO, executive, or other accountability target
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Person Type */}
        <div>
          <label className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
            Person Type *
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {personTypes.map((type) => (
              <label
                key={type.value}
                className={`flex items-start gap-2 p-3 border-2 rounded-lg cursor-pointer transition ${
                  formData.person_type === type.value
                    ? 'border-blue-500 bg-blue-500/5'
                    : 'border-cream-300 dark:border-navy-600 hover:border-blue-400'
                }`}
              >
                <input
                  type="radio"
                  name="person_type"
                  value={type.value}
                  checked={formData.person_type === type.value}
                  onChange={(e) => setFormData({ ...formData, person_type: e.target.value })}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-xs font-bold text-navy-900 dark:text-cream-50">{type.label}</p>
                  <p className="text-[10px] text-navy-500 dark:text-navy-300 mt-0.5">{type.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="name" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Brad S. Karp"
              className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
              required
            />
          </div>

          <div>
            <label htmlFor="first_name" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
              First Name
            </label>
            <input
              type="text"
              id="first_name"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              placeholder="Brad"
              className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
            />
          </div>

          <div>
            <label htmlFor="last_name" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
              Last Name
            </label>
            <input
              type="text"
              id="last_name"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              placeholder="Karp"
              className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="office_name" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
              {isCorporate ? 'Position/Company' : 'Office Name'} *
            </label>
            <input
              type="text"
              id="office_name"
              value={formData.office_name}
              onChange={(e) => setFormData({ ...formData, office_name: e.target.value })}
              placeholder={isCorporate ? "Chairman, Paul Weiss Rifkind Wharton & Garrison LLP" : "U.S. Senator from California"}
              className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
              required
            />
          </div>

          <div className="md:col-span-2">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label htmlFor="id" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
                  Unique ID *
                </label>
                <input
                  type="text"
                  id="id"
                  value={formData.id}
                  onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                  placeholder="ceo-brad-karp-001"
                  className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50 font-mono"
                  required
                />
              </div>
              <button
                type="button"
                onClick={generateId}
                className="px-4 py-2.5 bg-cream-200 dark:bg-navy-700 hover:bg-cream-300 dark:hover:bg-navy-600 text-navy-900 dark:text-cream-50 text-xs font-bold uppercase tracking-wider rounded-lg transition"
              >
                Generate
              </button>
            </div>
            <p className="text-[9px] text-navy-500 dark:text-navy-300 mt-1">
              Unique identifier like "ceo-brad-karp-001" or "sen-warren-ma"
            </p>
          </div>
        </div>

        {/* Ticker (only for corporate/CEO/executive) — drives real SEC EDGAR
            filing lookups instead of the LLM guessing a company from the
            position string. */}
        {isCorporate && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="ticker" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
                Stock Ticker
              </label>
              <input
                type="text"
                id="ticker"
                value={formData.ticker}
                onChange={(e) => setFormData({ ...formData, ticker: e.target.value.toUpperCase() })}
                placeholder="AAPL"
                className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
              />
              <p className="mt-1 text-[10px] text-navy-500 dark:text-navy-300">Enables real SEC EDGAR filing/insider-trade lookups. Leave blank if not publicly traded.</p>
            </div>
          </div>
        )}

        {/* Classification (only for officials) */}
        {!isCorporate && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label htmlFor="office_level" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
                Level
              </label>
              <select
                id="office_level"
                value={formData.office_level}
                onChange={(e) => setFormData({ ...formData, office_level: e.target.value })}
                className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
              >
                <option value="">Select...</option>
                {officeLevels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="office_branch" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
                Branch
              </label>
              <select
                id="office_branch"
                value={formData.office_branch}
                onChange={(e) => setFormData({ ...formData, office_branch: e.target.value })}
                className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
              >
                <option value="">Select...</option>
                {officeBranches.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="party" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
                Party
              </label>
              <select
                id="party"
                value={formData.party}
                onChange={(e) => setFormData({ ...formData, party: e.target.value })}
                className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
              >
                <option value="">Select...</option>
                {parties.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="chamber" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
                Chamber
              </label>
              <select
                id="chamber"
                value={formData.chamber}
                onChange={(e) => setFormData({ ...formData, chamber: e.target.value })}
                className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
              >
                <option value="">Select...</option>
                {chambers.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Geographic (only for officials) */}
        {!isCorporate && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="state" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
                State
              </label>
              <input
                type="text"
                id="state"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="CA"
                maxLength={2}
                className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50 uppercase"
              />
            </div>

            <div>
              <label htmlFor="district" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
                District
              </label>
              <input
                type="text"
                id="district"
                value={formData.district}
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                placeholder="12"
                className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
              />
            </div>
          </div>
        )}

        {/* Contact Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="website" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
              Website
            </label>
            <input
              type="url"
              id="website"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://example.com"
              className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="contact@example.com"
              className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(202) 555-0100"
              className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
            />
          </div>

          <div>
            <label htmlFor="photo_url" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
              Photo URL
            </label>
            <input
              type="url"
              id="photo_url"
              value={formData.photo_url}
              onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
              placeholder="https://example.com/photo.jpg"
              className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
            />
            <p className="text-[9px] text-navy-500 dark:text-navy-300 mt-1">
              Optional - photo agent can find this automatically
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-4 border-t border-cream-300 dark:border-navy-600">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-navy-600 dark:text-cream-300 hover:text-navy-900 dark:hover:text-cream-50 transition"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-5 py-2.5 text-xs font-bold uppercase tracking-wider bg-blue-600 hover:bg-blue-700 disabled:bg-navy-400 text-white rounded-lg transition"
          >
            {loading ? 'Adding...' : 'Add Official/CEO'}
          </button>
        </div>
      </form>
    </div>
  );
}
